<script setup lang="ts">
import {getCoinInfoBySymbol} from "@lib/getCoinInfo.ts";

const props = defineProps({
  crypto: {
    type: String,
    required: true
  },
  cryptoName: {
    type: String,
    required: true
  },
  quote: {
    type: String,
    required: true
  },
  quoteName: {
    type: String,
    required: true
  },
  converterRate: {
    type: Object as PropType<ConverterRateResponse>,
    required: true
  },
})

import {computed, type PropType} from 'vue';
import type {ConverterRateResponse} from "@lib/interfaces/converter.ts";
import {formatCurrency} from "@/lib/formatCurrencyNumbers.ts";
import {converterCurrencies, getSignFromQuote} from "@consts";

const validConversions = computed(() => {
  return props.converterRate?.conversions.filter(conv => conv.rate !== null);
});

const bestRate = computed(() => {
  return Math.max(...validConversions.value.map(conv => conv.rate));
});

const sign = getSignFromQuote(props.quote)


</script>

<template>
  <div class="coin-swap-paths bg-brand-background/90 p-6 rounded-xl border-2 border-brand-stroke/10">
    <h2 class="text-2xl font-bold mb-4 text-brand-primary">Coin Swap Paths</h2>
    <div v-for="(conversion, index) in validConversions" :key="index"
         class="conversion-row mb-6 p-4 bg-brand-background rounded-xl shadow-md"
         :class="{ 'border-2 border-brand-primary': conversion.rate === bestRate }">
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center">
          <img :src="getCoinInfoBySymbol(conversion.from).img_url" :alt="conversion.from" class="w-8 h-8 mr-2">
          <span class="font-semibold">{{ conversion.from }}</span>
          <span class="mx-2">→</span>
          <img :src="getCoinInfoBySymbol(conversion.to).img_url" :alt="conversion.to" class="w-8 h-8 mr-2">
          <span class="font-semibold">{{ conversion.to }}</span>
        </div>
        <div class="text-right">
          <div class="font-bold text-lg text-brand-primary">{{ formatCurrency(conversion.rate,sign) }}</div>
          <div class="text-sm text-brand-secondary">Rate</div>
        </div>
      </div>
      <div class="path-display flex items-center justify-center bg-brand-stroke/10 p-3 rounded-xl">
        <template v-for="(coin, cIndex) in conversion.path" :key="cIndex">
          <div class="flex items-center">
            <img :src="getCoinInfoBySymbol(coin).img_url" :alt="coin" class="w-6 h-6">
            <span class="ml-1">{{ coin }}</span>
          </div>
          <span v-if="cIndex < conversion.path.length - 1" class="mx-2">→</span>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.coin-swap-paths {
  font-family: Arial, sans-serif;
}
.conversion-row {
  transition: all 0.3s ease;
}
.conversion-row:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}
</style>